import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { DISTRICTS, districtLabel } from '../../lib/districts';
import type { EventCategory, EventPermit, HeritagePermit, Page, Referral, ReferralDecision, ReferralSourceType } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, SectionCard, Spinner, StatusBadge, Tabs } from '../../ui/components';

const EVENT_CATEGORIES: EventCategory[] = ['CULTURAL', 'SPORTS', 'COMMUNITY', 'RELIGIOUS', 'COMMERCIAL', 'OTHER'];

const TABS = [
  { key: 'event', label: 'Event Permits' },
  { key: 'heritage', label: 'Heritage Permits' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export function PermitsPage() {
  const [tab, setTab] = useState<TabKey>('event');

  return (
    <div>
      <PageHeader
        title="Event & Heritage Permit Processing"
        subtitle="Single-application permits that fan out referrals to POLICE_M05/FIRE_M06 or PLANNING_M17 — eventPermits.ts + heritagePermits.ts + referrals.ts."
      />
      <Tabs tabs={TABS} active={tab} onChange={setTab} />
      {tab === 'event' ? <EventPermitsTab /> : <HeritagePermitsTab />}
    </div>
  );
}

// ── Event permits ──────────────────────────────────────────────────────

function EventPermitsTab() {
  const { persona } = useAuth();
  const canManage = persona?.roles.includes('YOUTH_OFFICER') ?? false;
  const [status, setStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const query = new URLSearchParams({ limit: '100' });
  if (status) query.set('status', status);
  const permitsQ = useApiQuery<Page<EventPermit>>(`/api/v1/m30/event-permits?${query.toString()}`);
  const items = permitsQ.data?.items ?? [];
  const selected = items.find((p) => p.id === selectedId) ?? null;

  if (!canManage) {
    return <ErrorNote message="Processing event permits (referral responses, cancellation) requires the Youth & Sport Officer role — the calendar below only shows APPROVED events without it." />;
  }

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {(['SUBMITTED', 'REFERRALS_PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as const).map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
        <button className="btn-primary ml-auto" onClick={() => setShowCreate(true)}>
          + Log new application
        </button>
      </div>
      <div className="grid gap-5 lg:grid-cols-5">
        <div className="lg:col-span-3">
          {permitsQ.error ? <ErrorNote message={permitsQ.error} /> : null}
          <div className="card overflow-x-auto">
            {permitsQ.loading ? (
              <Spinner />
            ) : items.length === 0 ? (
              <EmptyState title="No event permits" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Event</th>
                    <th className="table-head">Venue</th>
                    <th className="table-head">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id} className={`cursor-pointer hover:bg-slate-50 ${selectedId === p.id ? 'bg-brand-50/60' : ''}`} onClick={() => setSelectedId(p.id)}>
                      <td className="table-cell">
                        <div className="font-medium">{p.eventName}</div>
                        <div className="text-[11px] text-slate-400">
                          {p.refNo} · {p.category} · {formatDateTime(p.startsAt)}
                        </div>
                      </td>
                      <td className="table-cell text-xs text-slate-500">{p.venueDescription}</td>
                      <td className="table-cell">
                        <StatusBadge status={p.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div className="lg:col-span-2">
          {selected ? (
            <EventPermitDetail key={selected.id} permit={selected} onChanged={permitsQ.reload} />
          ) : (
            <SectionCard>
              <EmptyState title="Select a permit" />
            </SectionCard>
          )}
        </div>
      </div>
      {showCreate ? (
        <CreateEventPermitModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            permitsQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function EventPermitDetail({ permit, onChanged }: { permit: EventPermit; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m30/event-permits/${permit.id}/cancel`);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Cancel failed');
    } finally {
      setBusy(false);
    }
  };

  const canCancel = (['SUBMITTED', 'REFERRALS_PENDING', 'APPROVED'] as string[]).includes(permit.status) && new Date(permit.startsAt).getTime() > Date.now();

  return (
    <div className="space-y-4">
      <SectionCard title={permit.eventName} actions={<StatusBadge status={permit.status} />}>
        {error ? (
          <div className="mb-2">
            <ErrorNote message={error} />
          </div>
        ) : null}
        <dl className="space-y-1.5 text-sm">
          <div className="flex justify-between">
            <dt className="text-slate-500">Applicant</dt>
            <dd className="font-medium text-slate-800">
              {permit.applicantName} ({permit.applicantType})
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">Venue</dt>
            <dd className="text-right font-medium text-slate-800">
              {permit.venueDescription}
              <div className="text-xs font-normal text-slate-500">{districtLabel(permit.venueDistrict)}</div>
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">When</dt>
            <dd className="font-medium text-slate-800">
              {formatDateTime(permit.startsAt)} – {formatDateTime(permit.endsAt)}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">Expected attendance</dt>
            <dd className="font-medium text-slate-800">{permit.expectedAttendance.toLocaleString()}</dd>
          </div>
          {permit.routeDescription ? (
            <div>
              <dt className="text-slate-500">Route</dt>
              <dd className="mt-0.5 text-xs text-slate-600">{permit.routeDescription}</dd>
            </div>
          ) : null}
          {permit.decisionReason ? (
            <div>
              <dt className="text-slate-500">Decision note</dt>
              <dd className="mt-0.5 text-xs text-slate-600">{permit.decisionReason}</dd>
            </div>
          ) : null}
        </dl>
        {canCancel ? (
          <button className="btn-danger mt-3 w-full" onClick={cancel} disabled={busy}>
            Cancel permit
          </button>
        ) : null}
      </SectionCard>

      <ReferralPanel sourceType="EVENT_PERMIT" sourceId={permit.id} allowedRole="YOUTH_OFFICER" onResolved={onChanged} />
    </div>
  );
}

function CreateEventPermitModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { persona } = useAuth();
  const [applicantName, setApplicantName] = useState(persona?.name ?? '');
  const [eventName, setEventName] = useState('');
  const [category, setCategory] = useState<EventCategory>('COMMUNITY');
  const [venueDescription, setVenueDescription] = useState('');
  const [venueDistrict, setVenueDistrict] = useState<string>(DISTRICTS[0]);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [expectedAttendance, setExpectedAttendance] = useState('100');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m30/event-permits', {
        applicantName,
        eventName,
        category,
        venueDescription,
        venueDistrict,
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        expectedAttendance: Number.parseInt(expectedAttendance, 10) || 1,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to submit permit application');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Log a new event permit application" onClose={onClose} wide>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          m30 derives the applicant from the caller's own token — there is no separate "on behalf of" field, so this is
          submitted as {persona?.name ?? 'the signed-in user'}. Submitting immediately fans out Referral rows to POLICE_M05
          and FIRE_M06.
        </p>
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Applicant name">
            <input className="field-input" value={applicantName} onChange={(e) => setApplicantName(e.target.value)} />
          </Field>
          <Field label="Event name">
            <input className="field-input" value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="e.g. Anse Royale Community Fun Run" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as EventCategory)}>
              {EVENT_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field label="District">
            <select className="field-input" value={venueDistrict} onChange={(e) => setVenueDistrict(e.target.value)}>
              {DISTRICTS.map((d) => (
                <option key={d} value={d}>
                  {districtLabel(d)}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Venue description">
          <input className="field-input" value={venueDescription} onChange={(e) => setVenueDescription(e.target.value)} placeholder="e.g. Stad Linite grounds, Victoria" />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Starts">
            <input type="datetime-local" className="field-input" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
          </Field>
          <Field label="Ends">
            <input type="datetime-local" className="field-input" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
          </Field>
          <Field label="Expected attendance">
            <input type="number" min="1" className="field-input" value={expectedAttendance} onChange={(e) => setExpectedAttendance(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || eventName.trim().length < 2 || venueDescription.trim().length < 2 || !startsAt || !endsAt}
          >
            {busy ? 'Submitting…' : 'Submit application'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Heritage permits ───────────────────────────────────────────────────

function HeritagePermitsTab() {
  const { persona } = useAuth();
  const canManage = persona?.roles.includes('HERITAGE_OFFICER') ?? false;
  const [status, setStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const query = new URLSearchParams({ limit: '100' });
  if (status) query.set('status', status);
  const permitsQ = useApiQuery<Page<HeritagePermit>>(canManage ? `/api/v1/m30/heritage-permits?${query.toString()}` : null);
  const items = permitsQ.data?.items ?? [];
  const selected = items.find((p) => p.id === selectedId) ?? null;

  if (!canManage) {
    return <ErrorNote message="Processing heritage permits requires the Heritage Officer role." />;
  }

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {(['SUBMITTED', 'REFERRALS_PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as const).map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
        <button className="btn-primary ml-auto" onClick={() => setShowCreate(true)}>
          + Log new application
        </button>
      </div>
      <div className="grid gap-5 lg:grid-cols-5">
        <div className="lg:col-span-3">
          {permitsQ.error ? <ErrorNote message={permitsQ.error} /> : null}
          <div className="card overflow-x-auto">
            {permitsQ.loading ? (
              <Spinner />
            ) : items.length === 0 ? (
              <EmptyState title="No heritage permits" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Monument / site</th>
                    <th className="table-head">Applicant</th>
                    <th className="table-head">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id} className={`cursor-pointer hover:bg-slate-50 ${selectedId === p.id ? 'bg-brand-50/60' : ''}`} onClick={() => setSelectedId(p.id)}>
                      <td className="table-cell">
                        <div className="font-medium">{p.monumentOrSiteName}</div>
                        <div className="text-[11px] text-slate-400">{p.refNo}</div>
                      </td>
                      <td className="table-cell text-xs text-slate-500">{p.applicantName}</td>
                      <td className="table-cell">
                        <StatusBadge status={p.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div className="lg:col-span-2">
          {selected ? (
            <HeritagePermitDetail key={selected.id} permit={selected} onChanged={permitsQ.reload} />
          ) : (
            <SectionCard>
              <EmptyState title="Select a permit" />
            </SectionCard>
          )}
        </div>
      </div>
      {showCreate ? (
        <CreateHeritagePermitModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            permitsQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function HeritagePermitDetail({ permit, onChanged }: { permit: HeritagePermit; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m30/heritage-permits/${permit.id}/cancel`);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Cancel failed');
    } finally {
      setBusy(false);
    }
  };

  const canCancel = (['SUBMITTED', 'REFERRALS_PENDING'] as string[]).includes(permit.status);

  return (
    <div className="space-y-4">
      <SectionCard title={permit.monumentOrSiteName} actions={<StatusBadge status={permit.status} />}>
        {error ? (
          <div className="mb-2">
            <ErrorNote message={error} />
          </div>
        ) : null}
        <dl className="space-y-1.5 text-sm">
          <div className="flex justify-between">
            <dt className="text-slate-500">Applicant</dt>
            <dd className="font-medium text-slate-800">
              {permit.applicantName} ({permit.applicantType})
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">Location</dt>
            <dd className="text-right font-medium text-slate-800">
              {permit.location}
              <div className="text-xs font-normal text-slate-500">{districtLabel(permit.district)}</div>
            </dd>
          </div>
          <div>
            <dt className="text-slate-500">Proposed works</dt>
            <dd className="mt-0.5 text-xs text-slate-600">{permit.workDescription}</dd>
          </div>
          {permit.decisionReason ? (
            <div>
              <dt className="text-slate-500">Decision note</dt>
              <dd className="mt-0.5 text-xs text-slate-600">{permit.decisionReason}</dd>
            </div>
          ) : null}
        </dl>
        {canCancel ? (
          <button className="btn-danger mt-3 w-full" onClick={cancel} disabled={busy}>
            Cancel permit
          </button>
        ) : null}
      </SectionCard>

      <ReferralPanel sourceType="HERITAGE_PERMIT" sourceId={permit.id} allowedRole="HERITAGE_OFFICER" onResolved={onChanged} />
    </div>
  );
}

function CreateHeritagePermitModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { persona } = useAuth();
  const [applicantName, setApplicantName] = useState(persona?.name ?? '');
  const [monumentOrSiteName, setMonumentOrSiteName] = useState('');
  const [location, setLocation] = useState('');
  const [district, setDistrict] = useState<string>(DISTRICTS[0]);
  const [workDescription, setWorkDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m30/heritage-permits', { applicantName, monumentOrSiteName, location, district, workDescription });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to submit permit application');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Log a new heritage permit application" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          Submitted as {persona?.name ?? 'the signed-in user'} (same caller-identity constraint as event permits). This
          immediately fans out one Referral to PLANNING_M17.
        </p>
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Applicant name">
          <input className="field-input" value={applicantName} onChange={(e) => setApplicantName(e.target.value)} />
        </Field>
        <Field label="Monument / site name">
          <input className="field-input" value={monumentOrSiteName} onChange={(e) => setMonumentOrSiteName(e.target.value)} placeholder="e.g. Mission Lodge" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Location">
            <input className="field-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Sans Souci Road" />
          </Field>
          <Field label="District">
            <select className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)}>
              {DISTRICTS.map((d) => (
                <option key={d} value={d}>
                  {districtLabel(d)}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Description of works">
          <textarea className="field-input" rows={3} value={workDescription} onChange={(e) => setWorkDescription(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || monumentOrSiteName.trim().length < 2 || location.trim().length < 2 || workDescription.trim().length < 10}
          >
            {busy ? 'Submitting…' : 'Submit application'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Shared referral fan-out panel ─────────────────────────────────────

function ReferralPanel({
  sourceType,
  sourceId,
  allowedRole,
  onResolved,
}: {
  sourceType: ReferralSourceType;
  sourceId: string;
  allowedRole: 'YOUTH_OFFICER' | 'HERITAGE_OFFICER';
  onResolved: () => void;
}) {
  const { persona } = useAuth();
  const canRespond = persona?.roles.includes(allowedRole) ?? false;
  const referralsQ = useApiQuery<{ items: Referral[] }>(`/api/v1/m30/referrals?sourceType=${sourceType}&sourceId=${sourceId}`);

  return (
    <SectionCard title="Referral fan-out">
      {referralsQ.loading ? (
        <Spinner label="Loading…" />
      ) : referralsQ.error ? (
        <ErrorNote message={referralsQ.error} />
      ) : (referralsQ.data?.items.length ?? 0) === 0 ? (
        <p className="text-sm text-slate-500">No referrals recorded.</p>
      ) : (
        <ul className="space-y-2">
          {referralsQ.data!.items.map((r) => (
            <ReferralRow
              key={r.id}
              referral={r}
              canRespond={canRespond}
              onDone={() => {
                referralsQ.reload();
                onResolved();
              }}
            />
          ))}
        </ul>
      )}
      {!canRespond ? (
        <p className="mt-2 text-[11px] text-slate-400">
          Recording a referee's decision on their behalf requires the {allowedRole.replaceAll('_', ' ')} role.
        </p>
      ) : null}
    </SectionCard>
  );
}

function ReferralRow({ referral, canRespond, onDone }: { referral: Referral; canRespond: boolean; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decision, setDecision] = useState<ReferralDecision>('NO_OBJECTION');
  const [comments, setComments] = useState('');

  const respond = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m30/referrals/${referral.id}/respond`, { decision, comments: comments || undefined, conditions: [] });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to record response');
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-lg bg-slate-50 px-3 py-2.5 text-sm">
      <div className="flex items-center justify-between">
        <span className="font-medium">{referral.targetMda.replaceAll('_', ' ')}</span>
        <StatusBadge status={referral.status} />
      </div>
      {referral.decision ? (
        <div className="mt-1 flex items-center gap-2 text-xs">
          <StatusBadge status={referral.decision} />
          {referral.comments ? <span className="text-slate-500">{referral.comments}</span> : null}
        </div>
      ) : null}
      {error ? <div className="mt-1 text-[11px] text-rose-600">{error}</div> : null}
      {referral.status === 'PENDING' && canRespond ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <select className="field-input !w-auto !py-1 text-xs" value={decision} onChange={(e) => setDecision(e.target.value as ReferralDecision)}>
            {(['NO_OBJECTION', 'CONDITIONAL', 'OBJECTION'] as const).map((d) => (
              <option key={d} value={d}>
                {d.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <input className="field-input !w-auto !py-1 text-xs" placeholder="comments (optional)" value={comments} onChange={(e) => setComments(e.target.value)} />
          <button className="btn-primary !px-2 !py-1 text-xs" onClick={respond} disabled={busy}>
            Record response
          </button>
        </div>
      ) : null}
    </li>
  );
}
